"use client";
import React, { FC, useState, useEffect } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { Route } from "next";
import { useAppSelector, useAppDispatch } from "@/app/lib/hooks";
import { getCmsEntryDataFaq } from "@/app/lib/reducers/faqReducer";
import ReactHtmlParser from "react-html-parser";
import { useTranslation } from "react-i18next";

interface CmsEntryData {
  data: any;
  title: string;
  content: string;
}

const CATEGORIES = [
  "our_tours",
  "booking_process",
  "payment_cancellation",
  "our_local_guides",
] as const;

const Chevron: FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      flexShrink: 0,
      transition: "transform .2s ease",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FaqComponent: FC = () => {
  const dispatch = useAppDispatch();
  const faq: { [key: string]: CmsEntryData[] } | null = useAppSelector(
    (state) => state.faq.faq
  );
  const selectedLanguage = useAppSelector(
    (state) => state.language.selectedLanguage
  );
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const [activeCategory, setActiveCategory] = useState<string>("our_tours");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { t } = useTranslation("guydeez");

  useEffect(() => {
    if (selectedLanguage && selectedLanguage !== "") {
      dispatch(
        getCmsEntryDataFaq(
          "cms-faqbooki-ng38-4420-8330-79ef29e19177",
          selectedLanguage,
          "booking_process"
        )
      );
      dispatch(
        getCmsEntryDataFaq(
          "cms-paymenta-ndca-ncel-lati-onhn52e19177",
          selectedLanguage,
          "payment_cancellation"
        )
      );
      dispatch(
        getCmsEntryDataFaq(
          "cms-c543da20-30ea-44b4-bba6-69048ff9934c",
          selectedLanguage,
          "our_tours"
        )
      );
      dispatch(
        getCmsEntryDataFaq(
          "cms-fb2eb895-b758-4eba-bd94-f5f5803fb97e",
          selectedLanguage,
          "our_local_guides"
        )
      );
    }
  }, [dispatch, selectedLanguage]);

  const selectCategory = (category: string) => {
    setActiveCategory(category);
    setActiveIndex(null);
  };

  // Toggle: clicking the open row closes it, otherwise opens the clicked one.
  const toggleAnswer = (index: number) => {
    setActiveIndex((cur) => (cur === index ? null : index));
  };

  const items =
    faq && faq[activeCategory] ? Object.values(faq[activeCategory]) : [];

  return (
    <div className="nc-FaqPage" style={{ background: "var(--g-cream)" }}>
      <div
        className="max-w-[860px] mx-auto"
        style={{ padding: "32px 20px 56px" }}
      >
        {/* Header */}
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--g-coral, #C13B5C)",
              fontWeight: 700,
              letterSpacing: ".1em",
              marginBottom: 6,
            }}
          >
            {t("faq_eyebrow", { defaultValue: "AIDE" })}
          </div>
          <h1
            style={{
              fontSize: "clamp(28px, 6vw, 40px)",
              color: "var(--g-burgundy)",
              fontWeight: 800,
              lineHeight: 1.1,
            }}
          >
            {t("faq_title")}
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "var(--g-ink-500)",
              marginTop: 8,
              maxWidth: 520,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {t("faq_subtitle", {
              defaultValue:
                "Trouvez rapidement une réponse à vos questions sur nos visites, réservations et guides locaux.",
            })}
          </p>
        </div>

        {/* Category tabs — 2×2 grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 8,
            marginBottom: 24,
          }}
        >
          {CATEGORIES.map((categoryKey) => {
            const active = activeCategory === categoryKey;
            return (
              <button
                key={categoryKey}
                type="button"
                onClick={() => selectCategory(categoryKey)}
                style={{
                  padding: "10px 8px",
                  borderRadius: 999,
                  border: active
                    ? "1.5px solid var(--g-burgundy)"
                    : "1.5px solid var(--g-line)",
                  background: active ? "var(--g-burgundy)" : "white",
                  color: active ? "white" : "var(--g-ink-700)",
                  fontSize: "clamp(11px, 3.2vw, 13px)",
                  fontWeight: active ? 700 : 600,
                  cursor: "pointer",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  transition: "background .15s, color .15s, border-color .15s",
                }}
              >
                {t("faq_" + categoryKey)}
              </button>
            );
          })}
        </div>

        {/* Accordion */}
        <div className="flex flex-col" style={{ gap: 12 }}>
          {items.length === 0 ? (
            <div
              className="gdx-card"
              style={{
                padding: 32,
                background: "white",
                borderColor: "transparent",
                textAlign: "center",
                color: "var(--g-ink-500)",
                fontSize: 14,
                boxShadow: "var(--g-shadow-sm, 0 2px 6px rgba(0,0,0,.04))",
              }}
            >
              {t("loading", { defaultValue: "Chargement…" })}
            </div>
          ) : (
            items.map((faqItem: CmsEntryData, faqIndex: number) => {
              const open = activeIndex === faqIndex;
              return (
                <div
                  key={faqIndex}
                  className="gdx-card"
                  style={{
                    background: "white",
                    borderColor: open ? "transparent" : "var(--g-line)",
                    boxShadow: open
                      ? "var(--g-shadow-md, 0 10px 30px rgba(0,0,0,.08))"
                      : "var(--g-shadow-sm, 0 2px 6px rgba(0,0,0,.04))",
                    overflow: "hidden",
                    transition: "box-shadow .2s ease",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleAnswer(faqIndex)}
                    aria-expanded={open}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      width: "100%",
                      padding: "18px 20px",
                      background: "transparent",
                      border: 0,
                      textAlign: "left",
                      cursor: "pointer",
                      color: open ? "var(--g-burgundy)" : "var(--g-ink-900, #1a1a1a)",
                      fontSize: 15,
                      fontWeight: 700,
                      fontFamily: "inherit",
                    }}
                  >
                    <span style={{ minWidth: 0 }}>{faqItem.data[0].title}</span>
                    <span
                      style={{
                        color: open
                          ? "var(--g-burgundy)"
                          : "var(--g-ink-500)",
                      }}
                    >
                      <Chevron open={open} />
                    </span>
                  </button>
                  {open && (
                    <div
                      className="faq-answer-content"
                      style={{
                        padding: "0 20px 20px",
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: "var(--g-ink-700, #4a4a4a)",
                      }}
                    >
                      {ReactHtmlParser(faqItem.data[0].content)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Contact CTA band */}
        <div
          className="gdx-card flex flex-col sm:flex-row items-center"
          style={{
            marginTop: 32,
            padding: 24,
            gap: 16,
            background: "white",
            borderColor: "transparent",
            boxShadow: "var(--g-shadow-sm, 0 2px 6px rgba(0,0,0,.04))",
            textAlign: "center",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: "var(--g-burgundy)",
                marginBottom: 4,
              }}
            >
              {t("faq_bottom")}
            </div>
            <div style={{ fontSize: 13, color: "var(--g-ink-500)" }}>
              {t("faq_bottom_sub", {
                defaultValue:
                  "Notre équipe vous répond en moins de 30 min, 7j/7.",
              })}
            </div>
          </div>
          <Link
            href={`/${lang}/${currency}/contact` as Route}
            className="inline-flex items-center justify-center"
            style={{
              flexShrink: 0,
              gap: 8,
              height: 46,
              padding: "0 24px",
              borderRadius: 999,
              background: "var(--g-green-ada, #1F7A45)",
              color: "white",
              fontSize: 14,
              fontWeight: 700,
              textDecoration: "none",
              boxShadow: "0 12px 26px rgba(31,122,69,.35)",
            }}
          >
            {t("account_help_cta", { defaultValue: "Contacter le support" })}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FaqComponent;
